import { BackgroundTransformer, ProcessorWrapper } from '@livekit/track-processors';
import { Track } from 'livekit-client';
import { useEffect, useRef, useState } from 'react';
import { BLUR_RADIUS, getSegmenterAssetPaths } from './backgroundConfig';
import { getSupportsBackgroundProcessors } from './getSupportsBackgroundProcessors';
/**
 * Размытие фона для лобби.
 *
 * В звонке процессор вешается на LocalVideoTrack через setProcessor, но в лобби
 * камера — это сырой getUserMedia-поток, никакого трека LiveKit ещё нет. Поэтому
 * поднимаем ProcessorWrapper напрямую: он принимает MediaStreamTrack и отдаёт
 * обработанный трек. Пайплайн тот же самый, что и в звонке, включая радиус
 * размытия и пути к ассетам, — поэтому предпросмотр честный.
 */
/**
 * Первый кадр в <video> приходит позже, чем резолвится play(). Процессор,
 * запущенный до него, падает в renderLoop с `InvalidStateError: Video element
 * has no video frame` — и падает на каждом кадре, пока жив.
 */
async function waitForVideoFrame(element, timeoutMs = 3000) {
    if (element.videoWidth > 0)
        return;
    await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            cleanup();
            reject(new Error('Camera produced no frame'));
        }, timeoutMs);
        const onFrame = () => {
            if (element.videoWidth === 0)
                return;
            cleanup();
            resolve();
        };
        function cleanup() {
            clearTimeout(timeout);
            element.removeEventListener('loadeddata', onFrame);
            element.removeEventListener('resize', onFrame);
        }
        element.addEventListener('loadeddata', onFrame);
        element.addEventListener('resize', onFrame);
    });
}
export function useLiveKitBackgroundPreview(sourceStream, enabled) {
    const [previewStream, setPreviewStream] = useState(null);
    const [isPreparing, setIsPreparing] = useState(false);
    const [error, setError] = useState(null);
    const isSupported = getSupportsBackgroundProcessors();
    // Скрытый <video> обязателен: ProcessorWrapper читает из него кадры.
    const sourceElementRef = useRef(null);
    useEffect(() => {
        const sourceTrack = sourceStream?.getVideoTracks()[0] ?? null;
        // Выключенная или уже остановленная камера кадров не даёт, а renderLoop
        // процессора падает на каждом кадре (InvalidStateError: no video frame) —
        // сотни исключений в секунду забивают вкладку и срывают вход в звонок.
        const isTrackRenderable = !!sourceTrack && sourceTrack.enabled && sourceTrack.readyState === 'live';
        if (!enabled || !isSupported || !isTrackRenderable) {
            setPreviewStream(null);
            setError(null);
            return;
        }
        let cancelled = false;
        let processor = null;
        const run = async () => {
            setIsPreparing(true);
            setError(null);
            const element = document.createElement('video');
            element.muted = true;
            element.playsInline = true;
            element.srcObject = new MediaStream([sourceTrack]);
            sourceElementRef.current = element;
            try {
                await element.play();
                await waitForVideoFrame(element);
                if (cancelled)
                    return;
                processor = new ProcessorWrapper(new BackgroundTransformer({
                    blurRadius: BLUR_RADIUS,
                    segmenterOptions: { delegate: 'GPU' },
                    assetPaths: getSegmenterAssetPaths(),
                }), 'lobby-background-blur');
                await processor.init({ kind: Track.Kind.Video, track: sourceTrack, element });
                if (cancelled) {
                    await processor.destroy();
                    return;
                }
                const processed = processor.processedTrack;
                if (!processed)
                    throw new Error('Processor returned no track');
                setPreviewStream(new MediaStream([processed]));
            }
            catch (err) {
                console.warn('Lobby background preview failed', err);
                if (!cancelled) {
                    setError('Не удалось включить размытие');
                    setPreviewStream(null);
                }
                await processor?.destroy().catch(() => undefined);
                processor = null;
            }
            finally {
                if (!cancelled)
                    setIsPreparing(false);
            }
        };
        run();
        // Камеру могут остановить, пока превью живёт (кнопка «Подключиться» гасит
        // поток лобби) — процессор нужно снять до того, как его renderLoop упрётся
        // в мёртвый трек.
        const handleTrackEnded = () => {
            cancelled = true;
            processor?.destroy().catch(() => undefined);
            processor = null;
            setPreviewStream(null);
        };
        sourceTrack.addEventListener('ended', handleTrackEnded);
        return () => {
            cancelled = true;
            sourceTrack.removeEventListener('ended', handleTrackEnded);
            processor?.destroy().catch(() => undefined);
            sourceElementRef.current?.pause();
            sourceElementRef.current = null;
            setPreviewStream(null);
        };
    }, [sourceStream, enabled, isSupported]);
    return { previewStream, isSupported, isPreparing, error };
}
